<template>
  <!-- [sc-46314] Il giro guidato per chi entra la prima volta. Montato in
       App.vue ma mostrato solo in home: è lì che si comincia, e una modale
       che ti aspetta dentro la scheda articolo sarebbe un agguato. -->
  <div
    v-if="daMostrare"
    class="onb-overlay"
    role="dialog"
    aria-modal="true"
    :aria-label="passoCorrente.titolo"
    tabindex="-1"
    ref="finestra"
    @keydown.esc="salta"
  >
    <div class="onb-card">
      <i class="onb-icon fa-solid" :class="passoCorrente.icona"></i>
      <h2 class="onb-titolo">{{ passoCorrente.titolo }}</h2>
      <!-- [sc-46314, review 48629] Una frase per riga: il testo di queste
           schermate si legge di corsa, e un blocco compatto si salta. -->
      <div class="onb-testi">
        <p v-for="(frase, i) in frasi" :key="i" class="onb-testo">
          {{ frase }}
        </p>
      </div>

      <!-- [sc-46314] I modi veri di raggiungerci. Compaiono solo se
           configurati: una schermata che dice «contattaci» senza dire come
           e' peggio di una che non lo dice. -->
      <div
        v-if="passoCorrente.contatto && contatti.length"
        class="onb-contatti"
      >
        <p class="onb-contatti__intro">Preferisci parlarne con noi?</p>
        <a
          v-for="contatto in contatti"
          :key="contatto.chiave"
          class="onb-contatto"
          :href="contatto.href"
          :target="contatto.esterno ? '_blank' : null"
          :rel="contatto.esterno ? 'noopener' : null"
          @click="tracciaContatto(contatto)"
        >
          <i class="fa-solid" :class="contatto.icona" aria-hidden="true"></i>
          {{ contatto.etichetta }}
        </a>
      </div>

      <div class="onb-pallini" aria-hidden="true">
        <span
          v-for="(p, i) in passi"
          :key="p.chiave"
          class="onb-pallino"
          :class="{ 'is-attivo': i === passo }"
        ></span>
      </div>

      <div class="onb-comandi">
        <button type="button" class="onb-salta" @click="salta">
          Salta il tutorial
        </button>
        <span class="onb-spazio"></span>
        <button
          v-if="passo > 0"
          type="button"
          class="onb-indietro"
          @click="indietro"
        >
          Indietro
        </button>
        <button v-if="!ultimo" type="button" class="onb-avanti" @click="avanti">
          Avanti
        </button>
        <button v-else type="button" class="onb-avanti" @click="finisci">
          Inizia
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { PASSI_BENVENUTO } from "@/config/onboarding";
import { benvenutoDaMostrare, inFrasi } from "@/utilities/onboarding";
import { linkPrenotaCall, mailtoAssistenza } from "@/utilities/abbonamento";
import { EVENTS, trackEvent } from "@/utilities/analytics";

export default {
  name: "OnboardingWelcome",
  data() {
    return { passo: 0 };
  },
  computed: {
    passi() {
      return PASSI_BENVENUTO;
    },
    passoCorrente() {
      return this.passi[this.passo] ?? this.passi[0];
    },
    ultimo() {
      return this.passo === this.passi.length - 1;
    },
    frasi() {
      return inFrasi(this.passoCorrente.testo);
    },
    /**
     * I modi di raggiungerci che esistono davvero.
     *
     * La call prima dell'email perche' e' quella che risolve in una volta;
     * l'email resta perche' oggi l'indirizzo dell'agenda non e' ancora
     * configurato, e senza di lei la schermata non direbbe come contattarci.
     */
    contatti() {
      const nome = this.$store.getters["company/branding"]?.name;
      const call = linkPrenotaCall();
      const email = mailtoAssistenza(nome);
      const elenco = [];
      if (call) {
        elenco.push({
          chiave: "call",
          etichetta: "Prenota una call",
          href: call,
          icona: "fa-calendar-check",
          esterno: true,
          evento: "book_call",
        });
      }
      if (email) {
        elenco.push({
          chiave: "email",
          etichetta: "Scrivici un'email",
          href: email,
          icona: "fa-envelope",
          esterno: false,
          evento: "contact_email",
        });
      }
      return elenco;
    },
    daMostrare() {
      // Niente finché il profilo non è arrivato: `currentUser` nullo vuol dire
      // «non lo so ancora», e mostrare adesso sarebbe un lampo.
      const user = this.$store.state.currentUser;
      if (!user) return false;
      return benvenutoDaMostrare({
        stato: this.$store.getters["onboarding/stato"],
        user,
        routeName: this.$route.name,
        configured: Boolean(
          this.$store.getters["company/branding"]?.configured
        ),
        readOnly: Boolean(this.$store.getters["company/readOnly"]),
      });
    },
  },
  watch: {
    daMostrare(visibile) {
      // Il fuoco entra nella finestra: da tastiera non si deve finire a
      // navigare la pagina che sta dietro, e senza fuoco `Esc` non arriva.
      if (visibile) this.$nextTick(() => this.$refs.finestra?.focus());
    },
  },
  mounted() {
    if (this.daMostrare) this.$refs.finestra?.focus();
  },
  methods: {
    avanti() {
      if (this.passo < PASSI_BENVENUTO.length - 1) this.passo += 1;
    },
    indietro() {
      if (this.passo > 0) this.passo -= 1;
    },
    salta() {
      return this.$store.dispatch("chiudiBenvenuto", { saltato: true });
    },
    finisci() {
      return this.$store.dispatch("chiudiBenvenuto", { saltato: false });
    },
    tracciaContatto(contatto) {
      // Solo un nome tecnico: il filtro di `analytics.js` non lascerebbe
      // passare altro, e non c'e' altro da sapere.
      trackEvent(EVENTS.FEATURE_USED, { feature: contatto.evento });
    },
  },
};
</script>

<style scoped>
.onb-overlay {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(15, 23, 42, 0.55);
}

.onb-card {
  width: min(460px, 100%);
  background: var(--page-bg, #fff);
  border-radius: 12px;
  padding: 28px 24px 20px;
  text-align: center;
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.28);
}

.onb-icon {
  font-size: 2rem;
  color: var(--brand-on-light, var(--brand));
}

.onb-titolo {
  margin: 12px 0 8px;
  font-size: 1.25rem;
}

.onb-testi {
  margin: 0 0 18px;
}

.onb-testo {
  margin: 0 0 6px;
  line-height: 1.5;
}

.onb-testo:last-child {
  margin-bottom: 0;
}

.onb-contatti {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 0 0 18px;
}

.onb-contatti__intro {
  width: 100%;
  margin: 0;
  font-size: 0.85rem;
  color: #64748b;
}

.onb-contatto {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border: 0.5px solid var(--brand-on-light, var(--brand));
  border-radius: 8px;
  color: var(--brand-on-light, var(--brand));
  text-decoration: none;
  font-size: 0.9rem;
}

.onb-contatto:hover {
  background: rgba(var(--brand-rgb, 15, 23, 42), 0.08);
}

.onb-pallini {
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-bottom: 18px;
}

.onb-pallino {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #cbd5e1;
}

.onb-pallino.is-attivo {
  background: var(--brand-on-light, var(--brand));
}

.onb-comandi {
  display: flex;
  align-items: center;
  gap: 8px;
}

.onb-spazio {
  flex: 1;
}

.onb-salta {
  background: none;
  border: 0;
  padding: 8px 4px;
  color: #64748b;
  text-decoration: underline;
  cursor: pointer;
}

.onb-indietro,
.onb-avanti {
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
}

.onb-indietro {
  border: 0.5px solid #cbd5e1;
  background: transparent;
}

.onb-avanti {
  border: 0;
  background: var(--brand);
  color: var(--brand-ink, #fff);
}

@media (prefers-reduced-motion: no-preference) {
  .onb-card {
    animation: onb-entra 0.18s ease-out;
  }
}

@keyframes onb-entra {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
}
</style>
